import { useEffect, useState, useContext } from "react";
import { useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { ExternalLinkIcon, TrashIcon, XIcon } from "@heroicons/react/outline";
import { useModal, WorkflowContext } from "../contexts";
import { MIMETYPE_ICON_MAP } from "../utils";
import { WorkflowAutomationItem } from "../interfaces";
import { deleteData, postData } from "../backend";
import useReviewItems from "../hooks/useReviewItems";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import { useTranslation } from "react-i18next";
import WorkflowReviewCompletedModal from "./WorkflowReviewCompletedModal";
import useUser from "../hooks/useUser";

function WorkflowReview() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { t } = useTranslation();

  const { user } = useUser();
  const { openModal } = useModal();

  const location = useLocation();
  const itemId = new URLSearchParams(location.search).get("item");

  const { id: workflowId } = useParams<{ id?: string }>();
  const { data: reviewItems, mutate } = useReviewItems(workflowId);

  const [currentItem, setCurrentItem] = useState<WorkflowAutomationItem>();

  const { workflow, showAutomationPanel } = useContext(WorkflowContext);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);

  const [currentState, setCurrentState] = useState<"INITIAL" | "APPROVING" | "APPROVED" | "DELETING">("INITIAL");

  function isReviewable(item: WorkflowAutomationItem): boolean {
    return (item.stage === "REVIEW_PENDING" && (!workflow.team || item.approvalRequests.length === 0 || item.approvalRequests.filter(a => a.status === "PENDING" && a.approver.id === user?.id).length > 0))
  }


  useEffect(() => {
    if (!(itemId && reviewItems))
      return;

    const reviewItem = reviewItems.find(item => item.id === itemId);
    if (!reviewItem) {
      history.push(url + "/outputs");
      return openModal(<WorkflowReviewCompletedModal />)
    }
    setCurrentItem(reviewItems.find(item => item.id === itemId));
    setCurrentOutputIndex(0);
  }, [itemId, reviewItems, openModal, history, url]);

  function setCurrentItemOutput(item: WorkflowAutomationItem, outputIndex: number) {
    history.push(url + "/review?item=" + item.id);
    setCurrentOutputIndex(outputIndex);
  }

  async function handleApprove() {
    setCurrentState("APPROVING");
    await postData(`/workflows/automation-items/${itemId}/approve/`)
      .then(setCurrentItem)
      .catch(console.error);

    setCurrentState("APPROVED");

    setTimeout(moveToNextItem, 1000);
  }

  async function handleDeleteItem() {
    setCurrentState("DELETING");
    await deleteData(`/workflows/automation-items/${itemId}/`)
      .catch(console.error);

    setTimeout(moveToNextItem, 1000);
  }

  function moveToNextItem() {
    mutate();
    setCurrentState("INITIAL");
    setCurrentOutputIndex(0);

    const revieableItems = reviewItems?.filter(item => isReviewable(item));
    const index = revieableItems!.findIndex(item => item.id === currentItem?.id);

    const nextIndex = index + 1;
    const nextItem = revieableItems![nextIndex];

    if (nextItem)
      return history.push(url + "/review?" + new URLSearchParams({ item: nextItem.id }).toString());

    const previousIndex = index - 1;
    const previousItem = revieableItems![previousIndex];

    if (previousItem)
      return history.push(url + "/review?" + new URLSearchParams({ item: previousItem.id }).toString());

    history.push(url + "/outputs");
  }

  if (!location.pathname.endsWith("/review"))
    return null;

  if (!currentItem || !user || !workflow)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className='w-24 h-24'>
          <img src="https://static.portant.co/portant-loading-white.svg" alt="Loading Logo" />
        </div>
      </div>
    );

  if (!isReviewable(currentItem))
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className="text-gray-500 font-gilroy text-lg">
          {t("This item is not available for review.")}
        </div>
      </div>
    );

  const currentOutput = currentItem.outputs[currentOutputIndex];

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/review")}>
      <TransitionWorkflowPageSideBar>
        <div className={`flex flex-col gap-1 h-full ${showAutomationPanel && "blur-sm"}`}>
          <div className="flex items-center justify-between font-semibold font-gilroy text-gray-600">
            {t("Documents within this item")} ({currentItem.outputs.length})
          </div>

          {currentItem.outputs.map((output, index) =>
            <div key={output.id} className="flex items-center gap-1 w-full ">
              <div className="flex items-center gap-1 w-full">
                <img className="h-4 w-4 flex-shrink-0" src={MIMETYPE_ICON_MAP[output.onedriveFile?.fileMimeType ?? output.file?.mimeType ?? ""]} alt="Google File Icon" />
                <div className={`font-gilroy truncate w-full ${output.id === currentOutput.id ? "text-blue font-semibold" : "text-gray-600 hover:font-semibold"} cursor-pointer`} onClick={() => setCurrentItemOutput(currentItem, index)}>
                  {output.onedriveFile?.fileName ?? output.file?.name}
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-8">
            {currentState === "INITIAL" &&
              <>
                <button className="btn btn-green text-sm h-10 w-full" onClick={handleApprove}>
                  {t("Approve and Process")}
                </button>
                <button className="group flex items-center justify-center rounded w-10 h-10 p-0 text-gray-400 border border-gray-400 hover:border-red hover:bg-red flex-shrink-0" onClick={handleDeleteItem}>
                  <TrashIcon className="w-5 h-5 group-hover:text-white" />
                </button>
              </>
            }
            {currentState === "APPROVING" &&
              <button className="btn btn-blue text-sm h-10 w-full" disabled>
                {t("Processing...")}
              </button>
            }
            {currentState === "DELETING" &&
              <button className="btn btn-red text-sm h-10 w-full" disabled>
                {t("Removing...")}
              </button>
            }
            {currentState === "APPROVED" &&
              <button className="btn btn-green text-sm h-10 w-full" disabled>
                {t("Approved")}
              </button>
            }
          </div>
        </div>
      </TransitionWorkflowPageSideBar>

      {currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent>
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.onedriveFile.fileMimeType]} alt="Microsoft Excel File Icon" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col h-full justify-center">
                <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.onedriveFile?.fileUrl, "_blank")}>
                  <div className="truncate max-w-[400px]">
                    {currentOutput.onedriveFile.fileName}
                  </div>
                  {currentOutput.onedriveFile.fileUrl &&
                    <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                  }
                </div>
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
          </div>
          <iframe title={t("Microsoft File Viewer")} height="1000" className="w-full h-full border border-gray-300 rounded overflow-hidden"
            src={currentOutput.onedriveFile.fileEmbedViewUrl} />
        </TransitionWorkflowPageContent>
      }

      {!currentOutput.onedriveFile &&
        <TransitionWorkflowPageContent className="relative">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={MIMETYPE_ICON_MAP[currentOutput.file?.mimeType ?? "application/vnd.google-apps.document"]} alt="Google File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(currentOutput.file?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {currentOutput.file?.name}
                </div>
                {currentOutput.file?.url &&
                  <ExternalLinkIcon className="h-4 w-4 ml-1 mb-1 group-hover:text-blue" />
                }
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url + "/outputs")} />
          </div>

          {(currentOutput.file && currentOutput.file.mimeType !== "application/pdf")
            ? <iframe title={t("Google Editor")} className="w-full h-full border border-gray-300 rounded overflow-hidden" src={`${currentOutput.file.url}?widget&rm=embedded`} />
            : <div className="m-auto">{t("Unable to display file")}</div>
          }
        </TransitionWorkflowPageContent>
      }

    </TransitionWorkflowPage>
  );

}

export default WorkflowReview;
